// Copyright © 2024 Move Closer
var RandomImageModuleUi_1;
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { BootstrapLink } from '../../dsl/front';
import { AbstractModuleUi } from '../_abstract/ui';
import { prepareImage } from '../_filters/prepare-image';
import { FileDataActions } from '../Image/Image.contracts';
import { ImageWithCaption } from '../_components/ImageWithCaption';
import VueEasyLightbox from 'vue-easy-lightbox';
let RandomImageModuleUi = RandomImageModuleUi_1 = class RandomImageModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        this.action = FileDataActions;
        this.image = null;
        this.link = '';
        this.useAsAd = false;
        this.hideAuthor = false;
        this.hideDescription = false;
        this.sponsored = false;
        /**
         * Determines state of full screen preview
         */
        this.isImageFullScreen = false;
    }
    mounted() {
        this.fetchRelated().then(() => {
            this.$forceUpdate();
            if (this.useAsAd) {
                this.eventBus.emit('ads:static:show', {
                    externalUrl: this.link
                });
            }
        });
    }
    get imageLink() {
        return {
            target: this.link,
            sponsored: this.sponsored,
            newTab: true
        };
    }
    async fetchRelated() {
        if (!this.module.content.images || this.module.content.images.length === 0) {
            this.onError();
            return;
        }
        const results = await Promise.all(this.module.content.images.map((item) => {
            return this.relatedService
                .resolve(item.image, {})
                .then((imageFile) => {
                let resolvedImage = imageFile;
                const { alt, author, caption } = resolvedImage;
                const inlineOverrides = { alt, author, caption };
                if (item.useAsAd) {
                    inlineOverrides.caption = this.$t('_.ad');
                }
                resolvedImage = RandomImageModuleUi_1.applyInlineOverrides(resolvedImage, inlineOverrides);
                const image = prepareImage(resolvedImage, this.imageRatio, 1920, this.module.isLazyLoaded);
                return { item, image };
            })
                .catch((e) => {
                console.warn(e);
                return null;
            });
        }));
        const resolvedImages = results.filter((x) => x !== null);
        if (resolvedImages.length === 0) {
            this.onError();
            return;
        }
        const totalWeight = resolvedImages.reduce((sum, res) => sum + Number(res.item.weight), 0);
        const rand = Math.random() * totalWeight;
        let cumulativeWeight = 0;
        let selectedImage = null;
        for (const resImage of resolvedImages) {
            cumulativeWeight += Number(resImage.item.weight);
            if (rand <= cumulativeWeight) {
                selectedImage = resImage;
                break;
            }
        }
        if (selectedImage) {
            this.image = selectedImage.image;
            const item = selectedImage.item;
            this.hideAuthor = item.hideAuthor || false;
            this.hideDescription = item.hideDescription || false;
            this.sponsored = false;
            if (item.useAsAd) {
                this.useAsAd = true;
                this.hideAuthor = true;
                this.hideDescription = false;
                this.sponsored = true;
            }
            if (item.redirectExt) {
                this.link = item.redirectExt;
            }
            else if (item.redirect && typeof item.redirect.value === 'number') {
                try {
                    const content = await this.relatedService.resolve(item.redirect, {});
                    this.link = content.urlPath;
                }
                catch (e) {
                    console.warn(e);
                }
            }
            this.onReady();
        }
        else {
            this.onError();
        }
    }
    sendEvent() {
        if (this.useAsAd) {
            this.eventBus.emit('ads:static:click', {
                externalUrl: this.link
            });
        }
    }
    get hasLink() {
        return !!this.link;
    }
    /**
     * Checks whether image can be displayed full screen
     */
    get canHaveFullScreenView() {
        return this.module.content.action === this.action.Preview;
    }
    /**
     * Closes full screen view of image
     */
    closeFullScreenPreview() {
        this.isImageFullScreen = false;
    }
    /**
     * Opens image in the full screen mode
     */
    openFullScreenPreview() {
        if (!this.canHaveFullScreenView) {
            return;
        }
        this.isImageFullScreen = true;
    }
    /**
     * Merges the passed-in source `ImageFile` with the additional inline overrides.
     *
     * @param imageFile - Source `ImageFile` object to merge INTO.
     * @param inlineOverrides - Values to inject into the `ImageFile` object.
     */
    static applyInlineOverrides(imageFile, inlineOverrides) {
        inlineOverrides = this.flushUndefinedOverrides(inlineOverrides);
        return { ...imageFile, ...inlineOverrides };
    }
    /**
     * Flushes (removes) the properties with the undefined values.
     *
     * @param inlineOverrides - Values to inject into the `ImageFile` object.
     */
    static flushUndefinedOverrides(inlineOverrides) {
        return Object.entries(inlineOverrides).reduce((acc, [key, value]) => (typeof value === 'undefined' ? acc : {
            ...acc,
            [key]: value
        }), {});
    }
};
RandomImageModuleUi = RandomImageModuleUi_1 = __decorate([
    Component({
        name: 'RandomImageModuleUi',
        components: {
            BootstrapLink,
            ImageWithCaption,
            VueEasyLightbox
        },
        async created() {
            await this.fetchRelated();
        },
        template: `
    <div>
    <template v-if="hasLink">
      <BootstrapLink :link="imageLink" class="text-decoration-none">
        <ImageWithCaption v-if="image || debug" v-bind="{ image }" :isLazy="module.isLazyLoaded"
                          :hideDescription="hideDescription"
                          :hideAuthor="hideAuthor"
                          @click="sendEvent"
                          class="module" :class="[debugClass, loadingClass]"/>
      </BootstrapLink>
    </template>

    <template v-else>
      <ImageWithCaption v-if="image || debug" v-bind="{ image }" :isLazy="module.isLazyLoaded"
                        :canPreview="canHaveFullScreenView"
                        :hideDescription="hideDescription"
                        :hideAuthor="hideAuthor"
                        class="module preview-image" :class="[debugClass, loadingClass]"
                        @click="openFullScreenPreview"/>
      <VueEasyLightbox
        v-if="image && canHaveFullScreenView"
        :visible="isImageFullScreen"
        :imgs="image.fullscreen"
        @hide="closeFullScreenPreview"/>
    </template>
    </div>
  `
    })
], RandomImageModuleUi);
export { RandomImageModuleUi };
